import { DEMO_DDM as CORE_DEMO_DDM, ddmFieldMap } from '@bloo-team/core-shell'

/**
 * Demo twins for the extractor detail (rule 6). The YAML and samples are the
 * captured accops-hyworks_controller-syslog (730) — trimmed to the parts the
 * offline simulator reads — plus a JSON-decoder extractor shaped like
 * AI-GEN-TEST. Keyed by the demo catalogue's ids (lib/extractors.ts).
 */

export const DEMO_YAML_HYWORKS = `schema-version: 1.0
extractor-id: 730
integration: Syslog
vendor-name: Accops
product-name: HyWorks Controller
source-name: HYWORKS-CONTROLLER
source-type: MANAGEMENT-SERVER
source-description: Extractor for HyWorks Controller Events
provides-streams:
- AUTHENTICATION
master-filters:
- ^(?:\\<\\d+\\>)?(?:[\\w\\-]+\\:\\s+)?\\d{4}\\-\\d{1,2}\\-\\d{1,2}\\s+\\d{2}\\:\\d{2}\\:\\d{2}\\,\\d+\\s+\\_\\S+\\s+\\_
event-details:
- first-match: \\_\\[(?:USER\\s+LOGGED\\s+IN|Client\\s+Logged\\s+in)\\].*?Username\\:?\\s+\\'?\\w+\\'?\\,
  decoder: regex
  decoder-regex: (?:\\<\\d+\\>)?(?:[\\w\\-]+\\:\\s+)?(?P<datetime>\\d{4}\\-\\d{1,2}\\-\\d{1,2}\\s+\\d{2}\\:\\d{2}\\:\\d{2}\\,\\d+)\\s+\\_(?P<username>\\S+)\\s+\\_(?P<organizationname>\\S+)\\s+\\_(?P<loglevel>\\w+)\\s+\\_\\[(?P<action>(?:USER\\s+LOGGED\\s+IN|Client\\s+Logged\\s+in))\\].*?Username\\:?\\s+\\'?(?P<user>\\w+)\\'?\\,(?:.*?Device\\s+IP\\s+(?P<src>[\\w\\.\\:]+)\\,)?
  event-key-format: '{action}'
  event-key-mapping:
    USER LOGGED IN:
      annotate:
        Stream: AUTHENTICATION
        Action: LOGIN
        Status: PASSED
      translate:
        src: SrcIP
        user: User
        AuthProto: AuthProto
    Client Logged in:
      annotate:
        Stream: AUTHENTICATION
        Action: LOGIN
        Status: PASSED
      translate:
        src: SrcIP
        user: User
        AuthProto: AuthProto
  fallback:
    annotate:
      EventName: Generic HyWorks Controller Event
      Stream: OTHER
  globals:
    translate:
      datetime: SystemTstamp
      username: System
- first-match: User\\s+session\\s+\\'?\\w+\\'?\\s+logged\\s+out
  decoder: regex
  decoder-regex: (?:\\<\\d+\\>)?(?:[\\w\\-]+\\:\\s+)?(?P<datetime>\\d{4}\\-\\d{1,2}\\-\\d{1,2}\\s+\\d{2}\\:\\d{2}\\:\\d{2}\\,\\d+)\\s+\\_(?P<username>\\S+)\\s+.*?User\\s+session\\s+\\'?(?P<user>\\w+)\\'?\\s+logged\\s+out
  event-key-format: LOGOUT
  event-key-mapping:
    LOGOUT:
      annotate:
        Stream: AUTHENTICATION
        Action: LOGOUT
        Status: PASSED
      translate:
        user: User
  globals:
    translate:
      datetime: SystemTstamp
      username: System
`

export const DEMO_SAMPLES_HYWORKS = [
  '<14>HyworksController: 2021-11-25 10:39:01,774 _HFVEN18660 _Default _INFO _[USER LOGGED IN]. User george logged in from the device Hychgsk-2001:db8::3 MaskedName Details :- Logged in from Hyryesk, Username alexanderia, Device IP 192.168.1.1, Device MAC192.168.1.1',
  '<14>HyworksController: 2022-01-10 11:15:57,108 _HyworksAdmin _Default _INFO _[ghtte] User session \'george\' logged out successfully. _ _EDCLog',
  '<14>HyworksController: 2021-11-25 10:37:54,964 _192.168.1.1 _Default _INFO _[DEVICE RE-REGISTERED]. Device HyDesk-10:5b:ad:b2:19:df with MAC ID 2001:db8::1 and IP Address 192.168.1.1 is now connected. _ _EDCLog',
]

export const DEMO_YAML_JSON_WAF = `extractor-id: 10002
schema-version: 1.0
integration: Custom
vendor-name: Haltdos
product-name: Web Application Firewall
source-name: WAF
source-type: Security Device
provides-streams:
- FIREWALL
- WEBFILTER
master-filters:
- '{"log_source":"haltdos".*?}'
event-details:
- first-match: '"action":"(RECORD|DROP)"'
  decoder: json
  event-key-format: '{action}'
  event-key-mapping:
    RECORD:
      annotate:
        Stream: FIREWALL
        Action: RECORD
        Status: PASSED
      translate:
        dst: DstIP
        src: SrcIP
        ua: UserAgent
        uri: URI
    DROP:
      annotate:
        Stream: WEBFILTER
        Action: DROP
        Status: FAILED
      translate:
        dst: DstIP
        src: SrcIP
        ua: UserAgent
        uri: URI
  fallback:
    annotate:
      EventName: Generic Haltdos WAF event
      Stream: OTHER
  globals:
    translate:
      server: Hostname
      country: SrcLOC
`

export const DEMO_SAMPLES_JSON_WAF = [
  '{"log_source":"haltdos","server":"ncerewwewef.com","country":"RU","dst":"203.0.113.10","src":"198.51.100.0","ua":"Mozilla/5.0 (compatible; Mail.RU_Bot/2.0)","uri":"/ncgold/Images/newfwef.png","action":"DROP","status":440}',
  '{"log_source":"haltdos","server":"pd.ncsdvsdvsv.com","country":"IN","dst":"198.51.100.0","src":"203.0.113.44","ua":"Mozilla/4.0 (compatible; MSIE 6.0)","uri":"/ncgold/Images/nrcdsvex.png","action":"RECORD","status":200}',
]

/** Which demo YAML a catalogue row opens with. Rows not listed show a generic single-stream YAML. */
export function demoDetailFor(extractorId: string, name: string, sourceName: string | null, streams: string[] = []): { yaml: string; samples: string[] } {
  if (extractorId.startsWith('10002')) return { yaml: DEMO_YAML_JSON_WAF, samples: DEMO_SAMPLES_JSON_WAF }
  if (extractorId.startsWith('124') || extractorId.startsWith('730')) return { yaml: DEMO_YAML_HYWORKS, samples: DEMO_SAMPLES_HYWORKS }
  const stream = streams[0] ?? 'AUTHENTICATION'
  const yaml = `schema-version: 1.0
extractor-id: ${extractorId.split('_')[0]}
integration: Syslog
source-name: ${sourceName ?? name.toUpperCase()}
source-type: UNKNOWN
provides-streams:
- ${stream}
master-filters:
- .
event-details:
- first-match: (?P<msg>.+)
  decoder: regex
  decoder-regex: ^(?P<host>\\S+)\\s+(?P<msg>.+)$
  event-key-format: EVENT
  event-key-mapping:
    EVENT:
      annotate:
        Stream: ${stream}
        Action: EVENT
      translate:
        host: System
  fallback:
    annotate:
      EventName: Generic ${name} event
      Stream: OTHER
`
  return { yaml, samples: [`${name}-host-01 sample event for ${name}`, `${name}-host-02 another sample`] }
}

/**
 * Demo data model, as the Extractors stages read it: `Record<stream, fields>`
 * over the ONE fixture in core-shell (`DEMO_DDM`, the `ddm/list` twin the Data
 * Model page and SynthAI read too). Same name and shape as before the move.
 */
export const DEMO_DDM: Record<string, string[]> = ddmFieldMap(CORE_DEMO_DDM)
